import uniqBy from 'lodash/uniqBy'
import orderBy from 'lodash/orderBy'
import orgamax from 'ampersand-app'
import lang from '@/lang'
// Deliberately NOT via the '@/modules/banking/services' barrel — it also loads bankingService,
// which drags in the banking helpers, the agreement modal and with them the
// '@/modules/base/components/inputs' barrel / react-tag-input, whose untransformed ESM breaks any
// isolated jest suite that reaches this duck (the embedded-invoice-review suites did, through
// rag-chat-section). Only BookingService is needed here.
import { BookingService } from '@/modules/banking/services/bookingService'
import { experimentalFeatures } from '@/modules/base/util/constants'

import { toast } from '@/modules/base/components/toast/toastConfig'
import { chartsOfAccountsRequestKey, coalesceChartsOfAccounts } from './_helpers/coalesceChartsOfAccounts'

const ACTION_PREFIX = 'taxCategories/main'
const SET_TAX_CATEGORIES = `${ACTION_PREFIX}/setTaxCategories`
const SET_FAVORITE_TAX_CATEGORIES = `${ACTION_PREFIX}/setFavoriteTaxCategories`
const SET_ACTIVE_TAX_CATEGORY = `${ACTION_PREFIX}/setActiveTaxCategory`
const SET_SEARCH_TERM = `${ACTION_PREFIX}/setSearchTerm`
const SET_ACCOUNT_TYPE = `${ACTION_PREFIX}/setAccountType`
const SET_SELECTED_POSITION = `${ACTION_PREFIX}/setSelectedPosition`
const SHOW_LOADER = `${ACTION_PREFIX}/showLoader`
const HIDE_LOADER = `${ACTION_PREFIX}/hideLoader`
const OPEN = `${ACTION_PREFIX}/open`
const CLOSE = `${ACTION_PREFIX}/close`
const SET_RAW_CATEGORIES = `${ACTION_PREFIX}/setRawCategories`
const UPDATE_CATEGORY_FAVORITE = `${ACTION_PREFIX}/updateCategoryFavorite`

const initialState = {
  open: false,
  isLoading: false,
  categories: [],
  activeCategory: null,
  searchTerm: null,
  favoriteCategories: [],
  accountType: [],
  selectedPosition: null,
  /**
   * @type {import('@/modules/banking/services/bookingService').ChartOfAccount[]}
   */
  rawCategories: [],
}

/**
 * Synthetic categories that are built on the client and prepended to the
 * categories returned by the chart of accounts.
 */
export const FAVORITES_CATEGORY = lang.taxCategoryPositions.drawer.favorites
export const ALL_ACCOUNTS_CATEGORY = lang.taxCategoryPositions.drawer.allAccounts

export const defaultAccountTypesBase = 'assets,others,monetary'
export const defaultAccountTypesForExpense = 'incomeExpenditures,income,invoice,expenditures' + ',' + defaultAccountTypesBase
export const defaultAccountTypesForRevenue = 'incomeExpenditures,income,invoice,expenditures' + ',' + defaultAccountTypesBase
export const allAccountTypes = 'expenditures,assets,income,others,monetary,debtor,creditor,incomeExpenditures,invoice,economicAssetAccounting'

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SHOW_LOADER: {
      return { ...state, isLoading: true }
    }

    case HIDE_LOADER: {
      return { ...state, isLoading: false }
    }

    case OPEN: {
      return { ...state, open: true, activeCategory: null }
    }

    case CLOSE: {
      return { ...state, open: false }
    }

    case SET_TAX_CATEGORIES: {
      const { categories } = action
      return { ...state, categories }
    }

    case SET_FAVORITE_TAX_CATEGORIES: {
      const { favoriteCategories } = action
      return { ...state, favoriteCategories }
    }

    case UPDATE_CATEGORY_FAVORITE: {
      const { account, favorite } = action

      const newFavoriteCategories = favorite
        ? [...state.favoriteCategories, account]
        : state.favoriteCategories.filter(category => category.id !== account.id)

      const newCategories = state.categories.map(category => ({
        ...category,
        accounts: category.accounts.find(currentAccount => currentAccount.id === account.id)
          ? orderBy(category.accounts.map(currentAccount => currentAccount.id === account.id ? { ...currentAccount, favorite } : currentAccount), ['favorite', 'accountNo'], ['desc', 'asc'])
          : category.accounts,
      }))

      const newRawCategories = state.rawCategories.map(currentAccount => currentAccount.id === account.id ? { ...currentAccount, favorite } : currentAccount)

      const favoriteCategory = newCategories.find(category => category.accountCategory === FAVORITES_CATEGORY)
      if (favoriteCategory) {
        favoriteCategory.accounts = orderBy(newRawCategories.filter(account => account.favorite), ['accountNo'], ['asc'])
      }

      return {
        ...state,
        favoriteCategories: newFavoriteCategories,
        rawCategories: newRawCategories,
        categories: newCategories,
        activeCategory: state.activeCategory ? newCategories.find(category => category.accountCategory === state.activeCategory.accountCategory) : newCategories[0],
      }
    }

    case SET_ACTIVE_TAX_CATEGORY: {
      const { category } = action
      return {
        ...state,
        activeCategory: category,
      }
    }

    case SET_SEARCH_TERM: {
      const { searchTerm } = action
      return { ...state, searchTerm }
    }

    case SET_ACCOUNT_TYPE: {
      const { accountType } = action
      return { ...state, accountType }
    }

    case SET_SELECTED_POSITION: {
      const { selectedPosition } = action
      return { ...state, selectedPosition }
    }

    case SET_RAW_CATEGORIES: {
      const { rawCategories } = action
      return { ...state, rawCategories }
    }

    default: {
      return state
    }
  }
}

const showLoader = () => ({ type: SHOW_LOADER })
const hideLoader = () => ({ type: HIDE_LOADER })

export const openDrawer = () => ({ type: OPEN })
export const closeDrawer = () => ({ type: CLOSE })
export const setCategories = (categories) => ({ type: SET_TAX_CATEGORIES, categories })
export const setActiveCategory = (category) => ({ type: SET_ACTIVE_TAX_CATEGORY, category })
export const setFavoriteCategories = (categories) => ({ type: SET_FAVORITE_TAX_CATEGORIES, favoriteCategories: categories })
export const setSearchTerm = (searchTerm) => ({ type: SET_SEARCH_TERM, searchTerm })
export const setAccountType = (accountType) => ({ type: SET_ACCOUNT_TYPE, accountType })
export const setSelectedPosition = (selectedPosition) => ({ type: SET_SELECTED_POSITION, selectedPosition })
export const setRawCategories = (rawCategories) => ({ type: SET_RAW_CATEGORIES, rawCategories })
export const updateAccountFavoriteState = (account, favorite) => ({ type: UPDATE_CATEGORY_FAVORITE, account, favorite })

export const updateAccountFavorite = (account) => {
  return (dispatch) => {
    const { favorite, id } = account

    return BookingService.chartOfAccounts.updateAccountFavorite(id, !favorite)
      .then(() => {
        dispatch(updateAccountFavoriteState(account, !favorite))
      })
      .catch(() => toast.showToast({ type: 'error', message: lang.taxCategorySaveError }))
  }
}

/**
 * Baseline chart-of-accounts fetch. Body unchanged from the pre-useDocuAi baseline; the only
 * addition is that it now returns its promise, so the flag-ON wrapper can await it. Every existing
 * caller discards the return value, so flag-OFF behaviour is unaffected.
 *
 * @param {import('@/modules/banking/services/bookingService').ChartOfAccountsListOptions} options
 * @returns {Promise<void>}
 */
const fetchChartsOfAccounts = (options = {}) => {
  return (dispatch, getState) => {
    const { searchTerm, accountType = allAccountTypes } = options
    const { taxCategories: { main: { activeCategory } } } = getState()
    dispatch(showLoader())
    return BookingService.chartOfAccounts.getList({ ...options, accountType })
      .then((result) => {
        const { data: { chartsOfAccount: { accounts } } } = result
        dispatch(setRawCategories(accounts))

        const categories = uniqBy(accounts, 'accountCategory')
          .map((account) => {
            const { accountCategory } = account
            const subAccounts = accounts.filter(acc => accountCategory === acc.accountCategory)
            const sortedSubAccounts = orderBy(subAccounts, ['favorite', 'accountNo'], ['desc', 'asc'])
            return { accountCategory, accounts: sortedSubAccounts }
          })
        const sortedCategories = orderBy(categories, ['accountCategory'], ['asc'])

        if (!searchTerm) {
          const favoriteAccounts = orderBy(accounts.filter(account => account.favorite), ['accountNo'], ['asc'])

          const favorites = {
            accounts: favoriteAccounts,
            accountCategory: FAVORITES_CATEGORY,
          }
          sortedCategories.unshift(favorites)
          dispatch(setFavoriteCategories(favoriteAccounts))

          // refresh active category if specified
          if (activeCategory) {
            const { accountCategory } = activeCategory
            const updatedActiveCategory = sortedCategories.find(category => category.accountCategory === accountCategory) || sortedCategories[0]
            dispatch(setActiveCategory(updatedActiveCategory))
          } else {
            dispatch(setActiveCategory(sortedCategories[0]))
          }
          dispatch(setCategories(sortedCategories))
        } else {
          if (accounts.length) {
            const allMatchingAccounts = {
              accounts: orderBy(accounts, ['favorite', 'accountNo'], ['desc', 'asc']),
              accountCategory: ALL_ACCOUNTS_CATEGORY,
            }
            sortedCategories.unshift(allMatchingAccounts)
          }
          dispatch(setActiveCategory(sortedCategories[0]))
          dispatch(setCategories(sortedCategories))
        }
      })
      .catch(() => toast.showToast({ type: 'error', message: lang.taxCategoriesListFetchError }))
      .finally(() => dispatch(hideLoader()))
  }
}

// FLAG:useDocuAi — single dispatch point for the chart-of-accounts fetch.
//
// The account list is global, identical for every consumer and ~150 KB, but every
// TaxCategorySelectInput and every position row fetches it on mount. Opening one
// "Eingangsrechnung prüfen" for a 47-position receipt therefore fired 47 identical requests in the
// same tick; under that self-inflicted load the API did not answer them all within
// `config.requestTimeout` (20 s) and every straggler rejected with its own timeout — one error
// toast each. On the useDocuAi review path, coalesce concurrent duplicates: one load, one store
// update, one toast.
//
// Flag-OFF dispatches the baseline fetch unchanged and keeps storming, deliberately: this duck is
// shared with the booking assistant, Stammdaten/supplier edit and the tax-categories drawer, which
// must stay byte-identical to the baseline.
/**
 * @param {import('@/modules/banking/services/bookingService').ChartOfAccountsListOptions} options
 * @returns {Promise<void>}
 */
export const getChartsOfAccounts = (options = {}) => {
  return (dispatch, getState) => {
    if (!orgamax.user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)) {
      return dispatch(fetchChartsOfAccounts(options))
    }

    return coalesceChartsOfAccounts(
      chartsOfAccountsRequestKey({ ...options, accountType: options.accountType ?? allAccountTypes }),
      () => dispatch(fetchChartsOfAccounts(options)),
    )
  }
}

export const getFavoriteCategories = (accountType = allAccountTypes) => {
  return (dispatch) => {
    return new Promise((resolve, reject) => {
      BookingService.chartOfAccounts.getList({ visibility: 'extended', accountType, favorite: true })
        .then(result => {
          const { data: { chartsOfAccount: { accounts } } } = result
          dispatch(setFavoriteCategories(accounts))
          resolve(accounts)
        })
        .catch((e) => reject(e))
    })
  }
}
